/**
 * Ranking Weights Configuration
 * 
 * Defines the weighting factors used to rank search results.
 * These weights are applied POST-FETCH since ZoomInfo doesn't support sorting.
 * 
 * How it works:
 * 1. User sets weights (or uses presets)
 * 2. Weights are sent to backend with search request
 * 3. Backend uses weights in scoreContact() to rerank results
 * 4. Higher weighted factors have more influence on final ranking
 */

// Weight categories for different search aspects
export const WEIGHT_CATEGORIES = {
  titleMatch: {
    id: 'titleMatch',
    label: 'Title Match',
    description: 'How closely the job title matches your search',
    icon: 'briefcase',
    defaultWeight: 80,
    maxPoints: 160, // Points awarded for perfect match
  },
  location: {
    id: 'location',
    label: 'Location',
    description: 'Geographic proximity to target location',
    icon: 'map-pin',
    defaultWeight: 60,
    maxPoints: 100, // Metro match points
  },
  seniority: {
    id: 'seniority',
    label: 'Seniority Level',
    description: 'Management level alignment with search criteria',
    icon: 'award',
    defaultWeight: 50,
    maxPoints: 40,
  },
  industry: {
    id: 'industry',
    label: 'Industry',
    description: 'Industry sector relevance',
    icon: 'building',
    defaultWeight: 40,
    maxPoints: 80,
  },
  contactQuality: {
    id: 'contactQuality',
    label: 'Contact Quality',
    description: 'Accuracy and completeness of contact information',
    icon: 'shield-check',
    defaultWeight: 30,
    maxPoints: 20,
  },
};

// Pre-configured weight presets for quick selection
export const WEIGHT_PRESETS = {
  balanced: {
    id: 'balanced',
    label: 'Balanced',
    description: 'Equal emphasis on all factors',
    weights: {
      titleMatch: 70,
      location: 60,
      seniority: 50,
      industry: 50,
      contactQuality: 40,
    },
  },
  titleFirst: {
    id: 'titleFirst',
    label: 'Title First',
    description: 'Prioritize exact role matches',
    weights: {
      titleMatch: 100,
      location: 40,
      seniority: 60,
      industry: 30,
      contactQuality: 20,
    },
  },
  locationFirst: {
    id: 'locationFirst',
    label: 'Location First',
    description: 'Prioritize geographic precision',
    weights: {
      titleMatch: 50,
      location: 100,
      seniority: 40,
      industry: 30,
      contactQuality: 20,
    },
  },
  qualityFirst: {
    id: 'qualityFirst',
    label: 'Quality First',
    description: 'Prioritize verified contact data',
    weights: {
      titleMatch: 50,
      location: 40,
      seniority: 40,
      industry: 30,
      contactQuality: 100,
    },
  },
  seniorityFirst: {
    id: 'seniorityFirst',
    label: 'Seniority First',
    description: 'Prioritize management level alignment',
    weights: {
      titleMatch: 60,
      location: 40,
      seniority: 100,
      industry: 40,
      contactQuality: 30,
    },
  },
};

// Default weights (balanced approach)
export const DEFAULT_WEIGHTS = WEIGHT_PRESETS.balanced.weights;

/**
 * Calculate the effective multiplier for a weight value
 * Maps 0-100 weight to 0.0-2.0 multiplier
 */
export function getWeightMultiplier(weight) {
  // 50 = 1.0x (baseline)
  // 0 = 0.0x (ignore this factor)
  // 100 = 2.0x (double importance)
  return weight / 50;
}

/**
 * Validate weights object has all required keys
 */
export function validateWeights(weights) {
  const requiredKeys = Object.keys(WEIGHT_CATEGORIES);
  const hasAllKeys = requiredKeys.every(key => 
    typeof weights[key] === 'number' && 
    weights[key] >= 0 && 
    weights[key] <= 100
  );
  return hasAllKeys;
}

/**
 * Merge partial weights with defaults
 */
export function mergeWithDefaults(partialWeights = {}) {
  return {
    ...DEFAULT_WEIGHTS,
    ...partialWeights,
  };
}

/**
 * Format weights for API request
 */
export function formatWeightsForAPI(weights) {
  return {
    rankingWeights: mergeWithDefaults(weights),
    useCustomRanking: true,
  };
}

const rankingWeightsExports = {
  WEIGHT_CATEGORIES,
  WEIGHT_PRESETS,
  DEFAULT_WEIGHTS,
  getWeightMultiplier,
  validateWeights,
  mergeWithDefaults,
  formatWeightsForAPI,
};

export default rankingWeightsExports;



